import { PersonSelect, type PersonOption } from '@/components/global/PersonSelect';
import {
    ResponsiveModal,
    ResponsiveModalContent,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert';
import { Button } from '@/components/ui/shadcn/button';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/shadcn/form';
import { Input } from '@/components/ui/shadcn/input';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { ProjectTask } from '@/types';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { router, usePage } from '@inertiajs/react';
import { Loader2, Plus } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useForm, type FieldPath } from 'react-hook-form';
import { toast } from 'sonner';
import { route } from 'ziggy-js';
import * as z from 'zod';

type CreateTaskFormValues = {
    name: string;
    start_date: string;
    end_date: string;
    assigned_to: string;
    parent_task_id: string;
    department_key: string;
};

const CREATE_TASK_FORM_FIELDS: (keyof CreateTaskFormValues)[] = ['name', 'start_date', 'end_date', 'assigned_to', 'parent_task_id', 'department_key'];

function applyInertiaValidationErrors(
    errors: Record<string, string | string[]>,
    setError: (name: FieldPath<CreateTaskFormValues>, error: { message: string }) => void,
): string | null {
    let firstError: string | null = null;
    for (const [key, raw] of Object.entries(errors)) {
        const message = Array.isArray(raw) ? raw[0] : raw;
        if (!message) {
            continue;
        }
        if (firstError === null) {
            firstError = message;
        }
        if (CREATE_TASK_FORM_FIELDS.includes(key as keyof CreateTaskFormValues)) {
            setError(key as FieldPath<CreateTaskFormValues>, { message });
        }
    }
    return firstError;
}

interface Props {
    projectId: string;
    users: { id: string; name: string }[];
    tasks: ProjectTask[];
    trigger?: ReactNode;
    isOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    initialParentTaskId?: string | null;
    /** The current user is the project owner — they may file a task into any department. */
    isProjectOwner?: boolean;
}

function getTodayYmd(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function addDaysToYmd(startYmd: string, days: number): string {
    const [y, m, d] = startYmd.split('-').map((part) => Number(part));
    if (!y || !m || !d) {
        return startYmd;
    }
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + days);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export default function CreateTaskModal({
    projectId,
    users,
    tasks,
    trigger,
    isOpen,
    onOpenChange,
    initialParentTaskId,
    isProjectOwner = false,
}: Props) {
    const { t } = useTranslations();
    const { settings } = useSettings();
    const page = usePage<{ locale?: string; auth?: { roles?: string[]; abilities?: string[] } }>();
    const locale = page.props.locale ?? 'en';
    const authRoles = page.props.auth?.roles ?? [];
    const authAbilities = page.props.auth?.abilities ?? [];
    const isAdministrator = authRoles.includes('Administrator');
    const canCreateTask = isAdministrator || authAbilities.includes('tasks.create');
    /**
     * Which department owns a task decides who may move it, so it is not a field
     * any creator should be able to choose — only an administrator or the person
     * responsible for the project. The endpoint applies the same rule, and the
     * edit sheet uses the same test.
     */
    const canSelectDepartment = isAdministrator || isProjectOwner;
    const [internalOpen, setInternalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);
    const open = isOpen ?? internalOpen;
    const setOpen = (value: boolean) => {
        if (onOpenChange) {
            onOpenChange(value);
            return;
        }
        setInternalOpen(value);
    };

    const parentTaskOptions = tasks.map((task) => ({
        value: task.id.toString(),
        label: task.name,
    }));

    // The caller passes a display name; PersonSelect wants the parts so it can
    // build initials for the avatar.
    const assigneeOptions = useMemo<PersonOption[]>(
        () =>
            users.map((user) => {
                const [first, ...rest] = (user.name || '').split(' ');

                return { id: user.id, first_name: first || user.name, last_name: rest.join(' ') };
            }),
        [users],
    );

    const roleLtMap = useMemo(() => {
        const map = new Map<string, string>();
        (settings.projectRoleTranslations ?? []).forEach((item) => {
            if (item?.value && item?.label_lt) {
                map.set(String(item.value), String(item.label_lt));
            }
        });
        return map;
    }, [settings.projectRoleTranslations]);

    const roleLabel = (roleName: string): string => {
        if (locale === 'lt') {
            return roleLtMap.get(roleName) ?? roleName;
        }
        return roleName;
    };

    // Roles switched off in Settings → System → Projects → Task configuration.
    const hiddenRoles = useMemo(
        () =>
            new Set(
                (settings.projectRoleTranslations ?? []).filter((item) => item?.value && item.enabled === false).map((item) => String(item.value)),
            ),
        [settings.projectRoleTranslations],
    );

    const departmentOptions = useMemo(() => {
        const roleDepartments = (settings.availableRoles ?? [])
            .filter((roleName) => !hiddenRoles.has(roleName))
            .map((roleName) => ({
                value: `role:${roleName}`,
                label: roleLabel(roleName),
                roles: [roleName],
            }));
        const customDepartments = (settings.projectTaskDepartments ?? [])
            .filter((item) => item?.value && item?.label && Array.isArray(item?.roles))
            .map((item) => ({
                value: String(item.value),
                label: String(item.label),
                roles: item.roles.filter((role): role is string => Boolean(role && typeof role === 'string')),
            }));
        return [...roleDepartments, ...customDepartments];
    }, [settings.availableRoles, settings.projectTaskDepartments, locale, roleLtMap, hiddenRoles]);
    const autoDepartmentForUser = useMemo(() => {
        for (const roleName of authRoles) {
            const matchingDepartment = (settings.projectTaskDepartments ?? []).find(
                (department) => Array.isArray(department?.roles) && department.roles.includes(roleName),
            );
            if (matchingDepartment?.value) {
                return String(matchingDepartment.value);
            }
        }

        const primaryRole = authRoles[0];
        if (!primaryRole) {
            return '';
        }

        return `role:${primaryRole}`;
    }, [authRoles, settings.projectTaskDepartments]);

    /**
     * The owner starts in their own department — the common case — but may file
     * the task elsewhere. An administrator has no department of their own, so
     * they start on the first option and pick deliberately.
     */
    const defaultDepartmentKey = useMemo(() => {
        if (!canSelectDepartment) {
            return autoDepartmentForUser;
        }

        if (!isAdministrator && autoDepartmentForUser !== '') {
            return autoDepartmentForUser;
        }

        return departmentOptions[0]?.value ?? '';
    }, [canSelectDepartment, isAdministrator, autoDepartmentForUser, departmentOptions]);

    const formSchema = useMemo(
        () =>
            z
                .object({
                    name: z
                        .string()
                        .min(1, t('projects.forms.edit_task.validation.name_required', 'Task name is required.'))
                        .max(
                            255,
                            t('projects.forms.task.validation.name_max', 'The task name may not be greater than :max characters.').replace(
                                ':max',
                                '255',
                            ),
                        ),
                    start_date: z.string().min(1, t('projects.forms.edit_task.validation.start_required', 'Start date is required.')),
                    end_date: z.string().min(1, t('projects.forms.edit_task.validation.end_required', 'End date is required.')),
                    assigned_to: z.string(),
                    parent_task_id: z.string(),
                    department_key: z.string(),
                })
                .refine((data) => data.start_date <= data.end_date, {
                    message: t('projects.forms.task.validation.end_after_or_equal', 'The end date must be on or after the start date.'),
                    path: ['end_date'],
                }),
        [t],
    );

    const form = useForm<CreateTaskFormValues>({
        resolver: standardSchemaResolver(formSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        defaultValues: {
            name: '',
            start_date: getTodayYmd(),
            end_date: '',
            assigned_to: '',
            parent_task_id: initialParentTaskId ?? '',
            department_key: defaultDepartmentKey,
        },
    });

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            form.reset({
                name: '',
                start_date: getTodayYmd(),
                end_date: '',
                assigned_to: '',
                parent_task_id: initialParentTaskId ?? '',
                department_key: defaultDepartmentKey,
            });
            setServerError(null);
            form.clearErrors();
        }
    };

    function onSubmit(values: CreateTaskFormValues) {
        setServerError(null);
        form.clearErrors();

        const resolvedDepartmentKey = canSelectDepartment ? values.department_key : autoDepartmentForUser;
        if (canSelectDepartment && resolvedDepartmentKey.trim() === '') {
            form.setError('department_key', {
                message: t('projects.forms.create_task.validation.department_required', 'Department is required.'),
            });
            onInvalid();
            return;
        }
        const selectedDepartment = departmentOptions.find((item) => item.value === resolvedDepartmentKey);
        const data = {
            name: values.name,
            description: null as string | null,
            start_date: values.start_date,
            end_date: values.end_date,
            status: 'not-started',
            type: 'other',
            assigned_to: values.assigned_to || null,
            parent_task_id: values.parent_task_id || null,
            department_key: selectedDepartment ? resolvedDepartmentKey : null,
            movable_by_roles: selectedDepartment ? selectedDepartment.roles : null,
        };

        router.post(route('projects.tasks.store', projectId), data, {
            preserveScroll: true,
            onStart: () => setIsSubmitting(true),
            onFinish: () => setIsSubmitting(false),
            onSuccess: () => {
                handleOpenChange(false);
            },
            onError: (errors) => {
                const other = applyInertiaValidationErrors(errors, form.setError);
                setServerError(other);
            },
        });
    }

    function onInvalid() {
        toast.error(t('projects.forms.create_task.validation.resolve_errors', 'Please fill in all required fields.'));
    }

    const applyDuration = (days: number) => {
        const currentStart = form.getValues('start_date') || getTodayYmd();
        form.setValue('end_date', addDaysToYmd(currentStart, Math.max(0, days - 1)), { shouldValidate: true });
    };

    useEffect(() => {
        if (!open) {
            return;
        }
        form.setValue('parent_task_id', initialParentTaskId ?? '', { shouldValidate: false });
    }, [open, initialParentTaskId, form]);

    if (!canCreateTask) {
        return null;
    }

    const requiredMark = <span className="text-destructive">*</span>;

    return (
        <ResponsiveModal open={open} onOpenChange={handleOpenChange} variant="sheet">
            <ResponsiveModalTrigger asChild>
                {trigger ?? (
                    <Button size="sm">
                        <Plus className="mr-2 h-4 w-4" />
                        {t('projects.forms.create_task.button', 'Create Task')}
                    </Button>
                )}
            </ResponsiveModalTrigger>
            {/* The form already owns the scrolling middle, so no ResponsiveModalBody here. */}
            <ResponsiveModalContent size="lg" drawerClassName="max-h-[min(90dvh,640px)]">
                <ResponsiveModalHeader className="shrink-0 space-y-1 border-b px-4 py-3 text-left">
                    <ResponsiveModalTitle className="text-base">{t('projects.forms.create_task.title', 'Create New Task')}</ResponsiveModalTitle>
                </ResponsiveModalHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="flex min-h-0 flex-1 flex-col" noValidate>
                        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-3">
                                {serverError ? (
                                    <Alert variant="destructive" className="sm:col-span-2">
                                        <AlertDescription>{serverError}</AlertDescription>
                                    </Alert>
                                ) : null}

                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem className="sm:col-span-2">
                                            <FormLabel className="text-xs">
                                                {t('projects.forms.create_task.labels.task_name', 'Task Name')} {requiredMark}
                                            </FormLabel>
                                            <FormControl>
                                                <Input {...field} className="h-9" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="start_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-xs">
                                                {t('projects.forms.create_task.labels.start_date', 'Start Date')} {requiredMark}
                                            </FormLabel>
                                            <FormControl>
                                                <DatePicker
                                                    value={field.value}
                                                    onValueChange={field.onChange}
                                                    placeholder={t('projects.forms.create_task.placeholders.select_start_date', 'Select start date')}
                                                    className="h-9 w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="end_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-xs">
                                                {t('projects.forms.create_task.labels.end_date', 'End Date')} {requiredMark}
                                            </FormLabel>
                                            <FormControl>
                                                <DatePicker
                                                    value={field.value}
                                                    onValueChange={field.onChange}
                                                    placeholder={t('projects.forms.create_task.placeholders.select_end_date', 'Select end date')}
                                                    className="h-9 w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                                    {[7, 14, 30, 90].map((days) => (
                                        <Button
                                            key={days}
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-8 text-xs"
                                            onClick={() => applyDuration(days)}
                                        >
                                            {days}d
                                        </Button>
                                    ))}
                                </div>

                                {canSelectDepartment ? (
                                    <FormField
                                        control={form.control}
                                        name="department_key"
                                        render={({ field }) => (
                                            <FormItem className="sm:col-span-2">
                                                <FormLabel className="text-xs">
                                                    {t('projects.forms.create_task.labels.department', 'Department')} {requiredMark}
                                                </FormLabel>
                                                <FormControl>
                                                    <Combobox
                                                        options={departmentOptions.map((item) => ({ value: item.value, label: item.label }))}
                                                        value={field.value}
                                                        onValueChange={field.onChange}
                                                        placeholder={t(
                                                            'projects.forms.create_task.placeholders.select_department',
                                                            'Select department',
                                                        )}
                                                        searchPlaceholder={t(
                                                            'projects.forms.create_task.placeholders.search_department',
                                                            'Search department...',
                                                        )}
                                                        className="h-9 w-full"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                ) : null}

                                <FormField
                                    control={form.control}
                                    name="assigned_to"
                                    render={({ field }) => (
                                        <FormItem className="sm:col-span-2">
                                            <FormLabel className="text-xs">
                                                {t('projects.forms.create_task.labels.assigned_to', 'Assigned To')}
                                            </FormLabel>
                                            <FormControl>
                                                <PersonSelect
                                                    value={field.value || null}
                                                    onValueChange={(v) => field.onChange(v ?? '')}
                                                    people={assigneeOptions}
                                                    variant="field"
                                                    // The sheet makes the rest of the page inert, which
                                                    // otherwise swallows clicks on the portalled list.
                                                    modal
                                                    labels={{
                                                        placeholder: t('projects.forms.create_task.placeholders.select_assignee', 'Select assignee'),
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="parent_task_id"
                                    render={({ field }) => (
                                        <FormItem className="sm:col-span-2">
                                            <FormLabel className="text-xs">
                                                {t('projects.forms.create_task.labels.parent_task', 'Parent Task')}
                                            </FormLabel>
                                            <FormControl>
                                                <Combobox
                                                    options={parentTaskOptions}
                                                    value={field.value}
                                                    onValueChange={field.onChange}
                                                    placeholder={t(
                                                        'projects.forms.create_task.placeholders.select_parent_task',
                                                        'Select parent task',
                                                    )}
                                                    searchPlaceholder={t('projects.forms.create_task.placeholders.search_tasks', 'Search tasks...')}
                                                    allowClear={true}
                                                    clearText={t('projects.forms.create_task.placeholders.no_parent_task', 'No parent task')}
                                                    className="h-9 w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
                            <div className="size-9 shrink-0" aria-hidden />
                            <div className="flex flex-1 justify-end gap-2 sm:flex-initial">
                                <Button type="button" variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
                                    {t('projects.forms.create_task.buttons.cancel', 'Cancel')}
                                </Button>
                                <Button type="submit" size="sm" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                                            {t('common.creating', 'Creating...')}
                                        </>
                                    ) : (
                                        t('projects.forms.create_task.buttons.create_task', 'Create Task')
                                    )}
                                </Button>
                            </div>
                        </div>
                    </form>
                </Form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
